import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Mail, CreditCard, Shield, Building2, Bell,
  Save, Play, Eye, EyeOff, CheckCircle, AlertCircle,
  Loader2, UserPlus, Trash2, Power, RefreshCw, X
} from 'lucide-react';

// ── Campo de formulário reutilizável ─────────────────────────
function Campo({ label, name, value, onChange, type = 'text', placeholder = '', hint, required }) {
  const [ver, setVer] = useState(false);
  const isPassword = type === 'password';
  return (
    <div>
      <label className="label">{label}{required && <span className="text-red-400 ml-0.5">*</span>}</label>
      <div className="relative">
        <input
          className="input pr-10"
          type={isPassword && !ver ? 'password' : 'text'}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
        {isPassword && (
          <button type="button" onClick={() => setVer(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            {ver ? <EyeOff size={14}/> : <Eye size={14}/>}
          </button>
        )}
      </div>
      {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function Toggle({ label, name, value, onChange, hint }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div>
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
      </div>
      <button type="button" onClick={() => onChange({ target: { name, value: value === 'true' ? 'false' : 'true' }})}
        className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 relative ${value === 'true' ? 'bg-acre-green' : 'bg-gray-200'}`}>
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${value === 'true' ? 'translate-x-4' : 'translate-x-0.5'}`}/>
      </button>
    </div>
  );
}

// ── Abas laterais ────────────────────────────────────────────
const ABAS = [
  { id: 'plataforma',   label: 'Dados da Plataforma', icon: Building2 },
  { id: 'email',        label: 'E-mail / SMTP',        icon: Mail       },
  { id: 'pagamento',    label: 'Pagamento',            icon: CreditCard },
  { id: 'notificacoes', label: 'Notificações',         icon: Bell       },
  { id: 'admins',       label: 'Administradores',      icon: Shield     },
];

// ── Modal novo admin ─────────────────────────────────────────
function ModalNovoAdmin({ onFechar, onCriado }) {
  const [form, setForm]     = useState({ nome: '', email: '', senha: '' });
  const [verSenha, setVerSenha] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const salvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      await api.post('/admin/admins', form);
      toast.success('Administrador criado!');
      onCriado();
      onFechar();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao criar admin.');
    }
    setSalvando(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="font-sora font-bold text-acre-dark">Novo Administrador</p>
            <p className="text-xs text-gray-400 mt-0.5">Acesso total ao painel admin</p>
          </div>
          <button onClick={onFechar} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
            <X size={14}/>
          </button>
        </div>
        <form onSubmit={salvar} className="space-y-3">
          <div>
            <label className="label">Nome completo *</label>
            <input className="input" required value={form.nome} onChange={e => setForm(p => ({...p, nome: e.target.value}))} placeholder="Ex: João Silva"/>
          </div>
          <div>
            <label className="label">E-mail *</label>
            <input className="input" type="email" required value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="admin@acrejob.com.br"/>
          </div>
          <div>
            <label className="label">Senha * (mín. 8 caracteres)</label>
            <div className="relative">
              <input className="input pr-16" type={verSenha ? 'text' : 'password'} required minLength={8} value={form.senha} onChange={e => setForm(p => ({...p, senha: e.target.value}))} placeholder="Senha segura"/>
              <button type="button" onClick={() => setVerSenha(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600 font-medium">
                {verSenha ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-xs text-yellow-700 flex items-start gap-2">
            <AlertCircle size={12} className="flex-shrink-0 mt-0.5"/>
            <span>Este usuário terá <strong>acesso total</strong> ao painel administrativo. Crie apenas para pessoas de confiança.</span>
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onFechar} className="btn-outline flex-1 py-2.5 text-sm justify-center">Cancelar</button>
            <button type="submit" disabled={salvando} className="btn-primary flex-1 py-2.5 text-sm justify-center flex items-center gap-2 disabled:opacity-60">
              {salvando ? <Loader2 size={13} className="animate-spin"/> : <UserPlus size={13}/>} Criar Admin
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Página principal ─────────────────────────────────────────
export default function AdminConfiguracoes() {
  const [abaAtiva, setAbaAtiva] = useState('plataforma');
  const [cfg,      setCfg]      = useState({});
  const [loading,  setLoading]  = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [admins,   setAdmins]   = useState([]);
  const [modalAdmin, setModalAdmin] = useState(false);
  const [testEmail, setTestEmail]   = useState('');
  const [testando,  setTestando]    = useState(false);

  // ── Carrega configurações ──────────────────────────────────
  const carregarCfg = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/configuracoes');
      // Flatten: { categoria: { chave: { valor, ... } } } → { chave: valor }
      const flat = {};
      Object.values(data).forEach(cat => {
        Object.entries(cat).forEach(([k, v]) => { flat[k] = v.valor; });
      });
      setCfg(flat);
    } catch { toast.error('Erro ao carregar configurações.'); }
    setLoading(false);
  }, []);

  const carregarAdmins = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/admins');
      setAdmins(data);
    } catch {}
  }, []);

  useEffect(() => { carregarCfg(); carregarAdmins(); }, [carregarCfg, carregarAdmins]);

  // ── Handler genérico de campo ──────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setCfg(p => ({ ...p, [name]: value }));
  };

  // ── Salvar grupo de campos ─────────────────────────────────
  const salvar = async (chaves) => {
    setSalvando(true);
    try {
      const payload = {};
      chaves.forEach(k => { payload[k] = cfg[k] ?? ''; });
      await api.put('/admin/configuracoes', { configuracoes: payload });
      toast.success('✅ Configurações salvas!');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao salvar.');
    }
    setSalvando(false);
  };

  // ── Teste de e-mail ────────────────────────────────────────
  const testarEmail = async () => {
    setTestando(true);
    try {
      const { data } = await api.post('/admin/configuracoes/testar-email', { email_destino: testEmail });
      toast.success(data.mensagem);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Falha no envio.');
    }
    setTestando(false);
  };

  // ── Toggle admin ativo ─────────────────────────────────────
  const toggleAdmin = async (id, ativo) => {
    try {
      await api.put(`/admin/admins/${id}/ativo`, { ativo: !ativo });
      toast.success(!ativo ? 'Admin ativado.' : 'Admin desativado.');
      carregarAdmins();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro.');
    }
  };

  if (loading) return (
    <Layout title="Configurações" subtitle="Configurações gerais da plataforma AcreJob">
      <div className="flex justify-center py-20">
        <Loader2 size={30} className="animate-spin text-acre-green"/>
      </div>
    </Layout>
  );

  return (
    <Layout title="⚙️ Configurações" subtitle="Integrações, dados e preferências do sistema">
      {modalAdmin && <ModalNovoAdmin onFechar={() => setModalAdmin(false)} onCriado={carregarAdmins}/>}

      <div className="flex gap-5 items-start">

        {/* ── Sidebar de abas ── */}
        <nav className="w-52 flex-shrink-0 sticky top-4">
          <div className="card p-2 space-y-0.5">
            {ABAS.map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => setAbaAtiva(id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all text-left ${
                  abaAtiva === id
                    ? 'bg-acre-green text-white'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-acre-dark'
                }`}>
                <Icon size={15} className="flex-shrink-0"/>
                {label}
              </button>
            ))}
          </div>
        </nav>

        {/* ── Conteúdo ── */}
        <div className="flex-1 min-w-0">

          {/* ════════════════════════════════════════════
              DADOS DA PLATAFORMA
          ════════════════════════════════════════════ */}
          {abaAtiva === 'plataforma' && (
            <div className="card p-6 space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 bg-acre-light rounded-xl flex items-center justify-center">
                  <Building2 size={17} className="text-acre-green"/>
                </div>
                <div>
                  <h2 className="font-sora font-bold text-acre-dark">Dados da Plataforma</h2>
                  <p className="text-xs text-gray-400">Informações exibidas no sistema e e-mails</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Campo label="Nome da Plataforma" name="plataforma_nome" value={cfg.plataforma_nome || ''} onChange={handleChange} placeholder="AcreJob" required/>
                <Campo label="CNPJ" name="plataforma_cnpj" value={cfg.plataforma_cnpj || ''} onChange={handleChange} placeholder="00.000.000/0001-00"/>
                <Campo label="E-mail de Contato" name="plataforma_email" value={cfg.plataforma_email || ''} onChange={handleChange} placeholder="contato@acrejob.com.br"/>
                <Campo label="Telefone" name="plataforma_fone" value={cfg.plataforma_fone || ''} onChange={handleChange} placeholder="68 3222-0000"/>
                <div className="col-span-2">
                  <Campo label="Endereço" name="plataforma_endereco" value={cfg.plataforma_endereco || ''} onChange={handleChange} placeholder="Rua, número, bairro"/>
                </div>
                <Campo label="Cidade" name="plataforma_cidade" value={cfg.plataforma_cidade || ''} onChange={handleChange} placeholder="Rio Branco"/>
                <Campo label="URL da Logo" name="plataforma_logo" value={cfg.plataforma_logo || ''} onChange={handleChange} placeholder="https://..."/>
              </div>

              {cfg.plataforma_logo && (
                <div className="border border-gray-100 rounded-xl p-3 flex items-center gap-3">
                  <img src={cfg.plataforma_logo} alt="Logo" className="w-12 h-12 object-contain rounded-lg border border-gray-100"/>
                  <p className="text-xs text-gray-500">Pré-visualização da logo</p>
                </div>
              )}

              <button onClick={() => salvar(['plataforma_nome','plataforma_cnpj','plataforma_email','plataforma_fone','plataforma_endereco','plataforma_cidade','plataforma_logo'])}
                disabled={salvando} className="btn-primary w-full justify-center py-3 flex items-center gap-2 disabled:opacity-60">
                {salvando ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Salvar Dados da Plataforma
              </button>
            </div>
          )}

          {/* ════════════════════════════════════════════
              E-MAIL / SMTP
          ════════════════════════════════════════════ */}
          {abaAtiva === 'email' && (
            <div className="card p-6 space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center">
                  <Mail size={17} className="text-blue-600"/>
                </div>
                <div>
                  <h2 className="font-sora font-bold text-acre-dark">Integração de E-mail (SMTP)</h2>
                  <p className="text-xs text-gray-400">Usado para envio de notificações, boas-vindas e confirmações</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-700 space-y-1">
                <p className="font-semibold">💡 Provedores recomendados:</p>
                <p>• <strong>Gmail:</strong> host smtp.gmail.com, porta 587, TLS ativado</p>
                <p>• <strong>Outlook:</strong> host smtp.office365.com, porta 587</p>
                <p>• <strong>SendGrid:</strong> host smtp.sendgrid.net, porta 587</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Campo label="Servidor SMTP" name="email_host" value={cfg.email_host || ''} onChange={handleChange} placeholder="smtp.gmail.com" required/>
                <Campo label="Porta" name="email_port" value={cfg.email_port || '587'} onChange={handleChange} placeholder="587"/>
                <Campo label="Usuário / Login" name="email_user" value={cfg.email_user || ''} onChange={handleChange} placeholder="seu@email.com" required/>
                <Campo label="Senha" name="email_pass" type="password" value={cfg.email_pass || ''} onChange={handleChange} placeholder="Senha ou App Password" hint="Deixe em branco para manter a senha atual"/>
                <Campo label="Nome do Remetente" name="email_from_name" value={cfg.email_from_name || 'AcreJob'} onChange={handleChange} placeholder="AcreJob"/>
                <Campo label="E-mail Remetente" name="email_from_addr" value={cfg.email_from_addr || ''} onChange={handleChange} placeholder="noreply@acrejob.com.br"/>
              </div>

              <Toggle label="Usar TLS/SSL" name="email_secure" value={cfg.email_secure || 'false'} onChange={handleChange} hint="Ative para porta 465 (SSL). Para porta 587, geralmente desativado"/>

              {/* Teste */}
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                <p className="text-sm font-semibold text-gray-700 mb-2">🧪 Testar Configuração</p>
                <div className="flex gap-2">
                  <input className="input text-sm flex-1" type="email" placeholder="E-mail para receber o teste" value={testEmail} onChange={e => setTestEmail(e.target.value)}/>
                  <button onClick={testarEmail} disabled={testando || !testEmail}
                    className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors disabled:opacity-60 whitespace-nowrap">
                    {testando ? <Loader2 size={13} className="animate-spin"/> : <Play size={13}/>} Testar
                  </button>
                </div>
              </div>

              <button onClick={() => salvar(['email_host','email_port','email_user','email_pass','email_from_name','email_from_addr','email_secure'])}
                disabled={salvando} className="btn-primary w-full justify-center py-3 flex items-center gap-2 disabled:opacity-60">
                {salvando ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Salvar Configurações de E-mail
              </button>
            </div>
          )}

          {/* ════════════════════════════════════════════
              PAGAMENTO
          ════════════════════════════════════════════ */}
          {abaAtiva === 'pagamento' && (
            <div className="card p-6 space-y-4">
              {/* Header */}
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-xl overflow-hidden flex-shrink-0 bg-[#009EE3]/10 flex items-center justify-center">
                  <CreditCard size={17} className="text-[#009EE3]"/>
                </div>
                <div>
                  <h2 className="font-sora font-bold text-acre-dark">Integração com Mercado Pago</h2>
                  <p className="text-xs text-gray-400">Checkout Pro — PIX, cartão, boleto e app MP</p>
                </div>
                <img
                  src="https://http2.mlstatic.com/frontend-assets/mp-web-navigation/ui-navigation/5.21.22/mercadopago/logo__large@2x.png"
                  alt="MercadoPago" className="ml-auto h-5 object-contain"
                  onError={e => e.target.style.display='none'}
                />
              </div>

              {/* Guia de configuração */}
              <div className="bg-[#009EE3]/5 border border-[#009EE3]/20 rounded-xl p-4 text-xs text-gray-700">
                <p className="font-semibold text-[#009EE3] mb-2">📋 Como obter as credenciais:</p>
                <ol className="space-y-1.5 list-decimal list-inside text-gray-600">
                  <li>Acesse <a href="https://www.mercadopago.com.br/developers" target="_blank" rel="noreferrer" className="text-[#009EE3] font-semibold underline">mercadopago.com.br/developers</a></li>
                  <li>Clique em <strong>Suas integrações → Criar aplicação</strong></li>
                  <li>Vá em <strong>Credenciais de teste</strong> para sandbox (começa com <code className="bg-gray-100 px-1 rounded">TEST-</code>)</li>
                  <li>Copie o <strong>Access Token</strong> e a <strong>Public Key</strong></li>
                  <li>Configure o Webhook em <strong>Webhooks → Criar</strong> apontando para:<br/>
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded mt-0.5 inline-block break-all">{cfg.frontend_url?.replace(':3000','') || 'https://seudominio.com'}/webhooks/mercadopago</code>
                  </li>
                  <li>Copie o <strong>Webhook Secret</strong> e cole abaixo</li>
                </ol>
                <div className="mt-2 flex gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                    ✅ <code>TEST-</code> = Sandbox (testes)
                  </span>
                  <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                    🚀 <code>APP_USR-</code> = Produção
                  </span>
                </div>
              </div>

              {/* Toggles */}
              <div className="bg-gray-50 rounded-xl p-4 divide-y divide-gray-100">
                <Toggle label="Integração Ativa" name="mp_ativo"
                  value={cfg.mp_ativo || 'false'} onChange={handleChange}
                  hint="Ative após configurar as credenciais. Desativado usa aprovação manual."/>
                <div className="pt-2">
                  <Toggle label="Ambiente Sandbox (testes)" name="mp_sandbox"
                    value={cfg.mp_sandbox || 'true'} onChange={handleChange}
                    hint="Use credenciais TEST- para testar sem cobrar de verdade"/>
                </div>
              </div>

              {/* Campos de credenciais */}
              <div className="grid grid-cols-1 gap-4">
                <Campo label="Access Token" name="mp_access_token" type="password"
                  value={cfg.mp_access_token || ''} onChange={handleChange}
                  placeholder="TEST-xxxx... ou APP_USR-xxxx..."
                  hint="Token privado — NUNCA compartilhe. Deixe em branco para manter o atual."/>
                <Campo label="Public Key" name="mp_public_key"
                  value={cfg.mp_public_key || ''} onChange={handleChange}
                  placeholder="TEST-xxxx... ou APP_USR-xxxx..."
                  hint="Chave pública usada no frontend do checkout"/>
                <Campo label="Webhook Secret" name="mp_webhook_secret" type="password"
                  value={cfg.mp_webhook_secret || ''} onChange={handleChange}
                  placeholder="Cole o secret gerado no painel MP"
                  hint="Necessário para validar notificações automáticas de pagamento"/>
                <Campo label="URL do Frontend" name="frontend_url"
                  value={cfg.frontend_url || 'http://localhost:3000'} onChange={handleChange}
                  placeholder="https://seudominio.com.br"
                  hint="URL base para os back_urls do Checkout Pro (retorno após pagamento)"/>
                <Campo label="URL do Webhook (pública)" name="mp_webhook_url"
                  value={cfg.mp_webhook_url || ''} onChange={handleChange}
                  placeholder="https://seudominio.com/webhooks/mercadopago"
                  hint="URL pública que o MP chamará. Em dev use ngrok ou similar."/>
              </div>

              {/* Status da integração */}
              <div className={`flex items-center gap-2.5 p-3 rounded-xl border text-sm font-medium ${
                cfg.mp_ativo === 'true'
                  ? 'bg-[#009EE3]/5 border-[#009EE3]/30 text-[#007ab5]'
                  : 'bg-gray-50 border-gray-200 text-gray-500'
              }`}>
                {cfg.mp_ativo === 'true'
                  ? <><CheckCircle size={15} className="text-[#009EE3]"/>
                      Integração Mercado Pago <strong>{cfg.mp_sandbox === 'true' ? 'ativa (SANDBOX)' : 'ativa (PRODUÇÃO)'}</strong>
                    </>
                  : <><AlertCircle size={15}/> Integração desativada — modo de aprovação manual</>}
              </div>

              <button onClick={() => salvar(['mp_access_token','mp_public_key','mp_webhook_secret','mp_ativo','mp_sandbox','mp_webhook_url','frontend_url'])}
                disabled={salvando}
                className="w-full py-3 rounded-xl font-bold text-sm bg-[#009EE3] hover:bg-[#007ab5] text-white flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                {salvando ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
                Salvar Configurações Mercado Pago
              </button>
            </div>
          )}

          {/* ════════════════════════════════════════════
              NOTIFICAÇÕES
          ════════════════════════════════════════════ */}
          {abaAtiva === 'notificacoes' && (
            <div className="card p-6 space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 bg-yellow-50 rounded-xl flex items-center justify-center">
                  <Bell size={17} className="text-yellow-600"/>
                </div>
                <div>
                  <h2 className="font-sora font-bold text-acre-dark">Notificações do Sistema</h2>
                  <p className="text-xs text-gray-400">Configure quando e como o sistema envia alertas</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 divide-y divide-gray-100">
                <Toggle label="Notificar admin em novo cadastro" name="notif_novo_cad" value={cfg.notif_novo_cad || 'true'} onChange={handleChange} hint="Envia e-mail ao admin quando empresa ou candidato se cadastrar"/>
                <div className="pt-2">
                  <Toggle label="Notificar admin em novo pagamento" name="notif_novo_pag" value={cfg.notif_novo_pag || 'true'} onChange={handleChange} hint="Envia e-mail ao admin quando empresa enviar comprovante"/>
                </div>
                <div className="pt-2">
                  <Toggle label="Notificar candidato em novo match" name="notif_match" value={cfg.notif_match || 'false'} onChange={handleChange} hint="Envia e-mail ao candidato quando surgir uma vaga compatível"/>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Campo label="E-mail do Admin (alertas)" name="notif_email_admin" value={cfg.notif_email_admin || ''} onChange={handleChange} placeholder="admin@acrejob.com.br" hint="Recebe todos os alertas do sistema"/>
                <Campo label="WhatsApp para alertas" name="notif_whatsapp" value={cfg.notif_whatsapp || ''} onChange={handleChange} placeholder="5568999999999" hint="Só números, com DDI e DDD"/>
              </div>

              <div>
                <label className="label">Dias antes do vencimento para avisar empresa</label>
                <div className="flex items-center gap-3">
                  {[3, 7, 14, 30].map(d => (
                    <button key={d} type="button"
                      onClick={() => setCfg(p => ({ ...p, notif_exp_dias: String(d) }))}
                      className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${
                        String(cfg.notif_exp_dias) === String(d)
                          ? 'bg-acre-green text-white border-acre-green'
                          : 'border-gray-200 text-gray-600 hover:border-acre-green'
                      }`}>
                      {d} dias
                    </button>
                  ))}
                  <input className="input w-24 text-sm" type="number" min="1" max="90" name="notif_exp_dias" value={cfg.notif_exp_dias || '7'} onChange={handleChange} placeholder="7"/>
                </div>
              </div>

              <button onClick={() => salvar(['notif_novo_cad','notif_novo_pag','notif_match','notif_email_admin','notif_whatsapp','notif_exp_dias'])}
                disabled={salvando} className="btn-primary w-full justify-center py-3 flex items-center gap-2 disabled:opacity-60">
                {salvando ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Salvar Configurações de Notificações
              </button>
            </div>
          )}

          {/* ════════════════════════════════════════════
              ADMINISTRADORES
          ════════════════════════════════════════════ */}
          {abaAtiva === 'admins' && (
            <div className="space-y-4">
              <div className="card p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-purple-50 rounded-xl flex items-center justify-center">
                      <Shield size={17} className="text-purple-600"/>
                    </div>
                    <div>
                      <h2 className="font-sora font-bold text-acre-dark">Administradores</h2>
                      <p className="text-xs text-gray-400">{admins.length} conta{admins.length !== 1 ? 's' : ''} com acesso total</p>
                    </div>
                  </div>
                  <button onClick={() => setModalAdmin(true)}
                    className="flex items-center gap-1.5 btn-primary text-sm px-4 py-2">
                    <UserPlus size={14}/> Novo Admin
                  </button>
                </div>

                <div className="space-y-2">
                  {admins.map(admin => (
                    <div key={admin.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${admin.ativo ? 'border-gray-100 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white font-bold flex-shrink-0">
                        {admin.nome?.charAt(0).toUpperCase()}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-sm text-acre-dark truncate">{admin.nome}</p>
                          {!admin.ativo && <span className="text-xs bg-red-50 text-red-500 border border-red-200 px-1.5 py-0.5 rounded-full">Inativo</span>}
                        </div>
                        <p className="text-xs text-gray-400 font-mono truncate">{admin.email}</p>
                        {admin.senha_teste && (
                          <p className="text-xs text-gray-300 mt-0.5">
                            Senha teste: <code className="bg-gray-100 text-gray-500 px-1 rounded">{admin.senha_teste}</code>
                          </p>
                        )}
                      </div>

                      {/* Datas */}
                      <div className="text-right flex-shrink-0 hidden sm:block">
                        <p className="text-xs text-gray-400">
                          {admin.ultimo_login
                            ? `Último login: ${new Date(admin.ultimo_login).toLocaleDateString('pt-BR')}`
                            : 'Nunca acessou'}
                        </p>
                        <p className="text-xs text-gray-300">
                          Criado: {new Date(admin.created_at).toLocaleDateString('pt-BR')}
                        </p>
                      </div>

                      {/* Ações */}
                      <button onClick={() => toggleAdmin(admin.id, admin.ativo)}
                        title={admin.ativo ? 'Desativar' : 'Ativar'}
                        className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all border ${
                          admin.ativo
                            ? 'border-red-200 text-red-400 hover:bg-red-50'
                            : 'border-green-200 text-green-500 hover:bg-green-50'
                        }`}>
                        <Power size={14}/>
                      </button>
                    </div>
                  ))}

                  {admins.length === 0 && (
                    <div className="py-8 text-center text-gray-400">
                      <p className="text-3xl mb-2">🛡️</p>
                      <p className="text-sm">Nenhum administrador cadastrado.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-xs text-yellow-700">
                <p className="font-semibold mb-1">⚠️ Atenção</p>
                <p>Administradores têm acesso total ao sistema: dados de usuários, pagamentos, configurações e logs. Crie contas apenas para pessoas autorizadas. Desative contas inativas.</p>
              </div>
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
}
